import { Copy, extendArray, UuidGenerator, Uuids } from '@standardnotes/utils';
import { SNLog } from '../../Log';
import { SplitPayloadsByEncryptionType, CreateEncryptionSplitWithKeyLookup, isErrorDecryptingParameters, } from '@standardnotes/encryption';
import { AbstractService, StoragePersistencePolicies, ApplicationEvent, ApplicationStage, ValueModesKeys, StorageValueModes, namespacedKey, RawStorageKey, } from '@standardnotes/services';
import { CreateDecryptedLocalStorageContextPayload, CreateDeletedLocalStorageContextPayload, CreateEncryptedLocalStorageContextPayload, CreatePayloadSplitWithDiscardables, DecryptedPayload, EncryptedPayload, isEncryptedLocalStoragePayload, PayloadTimestampDefaults, } from '@standardnotes/models';
import { ContentType } from '@standardnotes/domain-core';
/**
 * The storage service is responsible for persistence of both simple key-values, and payload
 * storage. It does so by relying on deviceInterface to save and retrieve raw values and payloads.
 * For simple key/values, items are grouped together in an in-memory hash, and persisted to disk
 * as a single object (encrypted, when possible). It handles persisting payloads in the local
 * database by encrypting the payloads when possible.
 * The storage service also exposes methods that allow the application to initially
 * decrypt the persisted key/values, and also a method to determine whether a particular
 * key can decrypt wrapped storage.
 */
export class DiskStorageService extends AbstractService {
    constructor(device, identifier, internalEventBus) {
        super(internalEventBus);
        this.device = device;
        this.identifier = identifier;
        this.internalEventBus = internalEventBus;
        this.storagePersistable = false;
        this.needsPersist = false;
        void this.setPersistencePolicy(StoragePersistencePolicies.Default);
    }
    provideEncryptionProvider(provider) {
        this.encryptionProvider = provider;
    }
    deinit() {
        ;
        this.device = undefined;
        this.encryptionProvider = undefined;
        this.storagePersistable = false;
        super.deinit();
    }
    async handleEvent(event) {
        if (event.type === ApplicationEvent.ApplicationStageChanged) {
            const stage = event.payload.stage;
            if (stage === ApplicationStage.Launched_10) {
                this.storagePersistable = true;
                if (this.needsPersist) {
                    void this.persistValuesToDisk();
                }
            }
        }
    }
    async setPersistencePolicy(persistencePolicy) {
        this.persistencePolicy = persistencePolicy;
        if (this.persistencePolicy === StoragePersistencePolicies.Ephemeral) {
            await this.device.clearNamespacedKeychainValue(this.identifier);
            await this.device.removeAllDatabaseEntries(this.identifier);
            await this.device.removeRawStorageValuesForIdentifier(this.identifier);
            await this.clearAllPayloads();
        }
    }
    isEphemeralSession() {
        return this.persistencePolicy === StoragePersistencePolicies.Ephemeral;
    }
    async initializeFromDisk() {
        const value = await this.device.getRawStorageValue(this.getPersistenceKey());
        const values = value ? JSON.parse(value) : undefined;
        await this.setInitialValues(values);
    }
    async setInitialValues(values) {
        const sureValues = values || this.defaultValuesObject();
        if (!sureValues[ValueModesKeys.Unwrapped]) {
            sureValues[ValueModesKeys.Unwrapped] = {};
        }
        this.values = sureValues;
        if (!this.isStorageWrapped()) {
            this.values[ValueModesKeys.Unwrapped] = {
                ...this.values[ValueModesKeys.Wrapped].content,
                ...this.values[ValueModesKeys.Unwrapped],
            };
        }
    }
    isStorageWrapped() {
        const wrappedValue = this.values[ValueModesKeys.Wrapped];
        return wrappedValue != undefined && isEncryptedLocalStoragePayload(wrappedValue);
    }
    async canDecryptWithKey(key) {
        const wrappedValue = this.values[ValueModesKeys.Wrapped];
        if (!isEncryptedLocalStoragePayload(wrappedValue)) {
            throw Error('Attempting to decrypt non decrypted storage value');
        }
        const decryptedPayload = await this.decryptWrappedValue(wrappedValue, key);
        return !isErrorDecryptingParameters(decryptedPayload);
    }
    async decryptWrappedValue(wrappedValue, key) {
        /**
         * The read content type doesn't matter, so long as we know it responds
         * to content type. This allows a more seamless transition when both web
         * and mobile used different content types for encrypted storage.
         */
        if (!(wrappedValue === null || wrappedValue === void 0 ? void 0 : wrappedValue.content_type)) {
            throw Error('Attempting to decrypt nonexistent wrapped value');
        }
        const payload = new EncryptedPayload({
            ...wrappedValue,
            ...PayloadTimestampDefaults(),
            content_type: ContentType.TYPES.EncryptedStorage,
        });
        const split = key
            ? {
                usesRootKey: {
                    items: [payload],
                    key: key,
                },
            }
            : {
                usesRootKeyWithKeyLookup: {
                    items: [payload],
                },
            };
        const decryptedPayload = await this.encryptionProvider.decryptSplitSingle(split);
        return decryptedPayload;
    }
    async decryptStorage() {
        const wrappedValue = this.values[ValueModesKeys.Wrapped];
        if (!isEncryptedLocalStoragePayload(wrappedValue)) {
            throw Error('Attempting to decrypt already decrypted storage');
        }
        const decryptedPayload = await this.decryptWrappedValue(wrappedValue);
        if (isErrorDecryptingParameters(decryptedPayload)) {
            throw SNLog.error(Error('Unable to decrypt storage.'));
        }
        this.values[ValueModesKeys.Unwrapped] = Copy(decryptedPayload.content);
    }
    /** @todo This function should be debounced. */
    async persistValuesToDisk() {
        if (!this.storagePersistable) {
            this.needsPersist = true;
            return;
        }
        if (this.persistencePolicy === StoragePersistencePolicies.Ephemeral) {
            return;
        }
        await this.currentPersistPromise;
        this.needsPersist = false;
        const values = await this.immediatelyPersistValuesToDisk();
        /** Save the persisted value so we have access to it in memory (for unit tests afawk) */
        this.values[ValueModesKeys.Wrapped] = values[ValueModesKeys.Wrapped];
    }
    async awaitPersist() {
        await this.currentPersistPromise;
    }
    async immediatelyPersistValuesToDisk() {
        this.currentPersistPromise = this.executeCriticalFunction(async () => {
            var _a;
            const values = await this.generatePersistableValues();
            const persistencePolicySuddenlyChanged = this.persistencePolicy === StoragePersistencePolicies.Ephemeral;
            if (persistencePolicySuddenlyChanged) {
                return values;
            }
            await ((_a = this.device) === null || _a === void 0 ? void 0 : _a.setRawStorageValue(this.getPersistenceKey(), JSON.stringify(values)));
            return values;
        });
        return this.currentPersistPromise;
    }
    /**
     * Generates a payload that can be persisted to disk,
     * either as a plain object, or an encrypted item.
     */
    async generatePersistableValues() {
        const rawContent = Copy(this.values);
        const valuesToWrap = rawContent[ValueModesKeys.Unwrapped];
        rawContent[ValueModesKeys.Unwrapped] = undefined;
        const payload = new DecryptedPayload({
            uuid: UuidGenerator.GenerateUuid(),
            content: valuesToWrap,
            content_type: ContentType.TYPES.EncryptedStorage,
            ...PayloadTimestampDefaults(),
        });
        if (this.encryptionProvider.hasRootKeyEncryptionSource()) {
            const split = {
                usesRootKeyWithKeyLookup: {
                    items: [payload],
                },
            };
            const encryptedPayload = await this.encryptionProvider.encryptSplitSingle(split);
            rawContent[ValueModesKeys.Wrapped] = CreateEncryptedLocalStorageContextPayload(encryptedPayload);
        }
        else {
            rawContent[ValueModesKeys.Wrapped] = CreateDecryptedLocalStorageContextPayload(payload);
        }
        return rawContent;
    }
    setValue(key, value, mode = StorageValueModes.Default) {
        this.setValueWithNoPersist(key, value, mode);
        void this.persistValuesToDisk();
    }
    async setValueAndAwaitPersist(key, value, mode = StorageValueModes.Default) {
        this.setValueWithNoPersist(key, value, mode);
        await this.persistValuesToDisk();
    }
    setValueWithNoPersist(key, value, mode = StorageValueModes.Default) {
        if (!this.values) {
            throw Error(`Attempting to set storage key ${key} before loading local storage.`);
        }
        const domainKey = this.domainKeyForMode(mode);
        const domainStorage = this.values[domainKey];
        domainStorage[key] = value;
    }
    getValue(key, mode = StorageValueModes.Default, defaultValue) {
        if (!this.values) {
            throw Error(`Attempting to get storage key ${key} before loading local storage.`);
        }
        if (!this.values[this.domainKeyForMode(mode)]) {
            throw Error(`Storage domain mode not available ${mode} for key ${key}`);
        }
        const value = this.values[this.domainKeyForMode(mode)][key];
        return value != undefined ? value : defaultValue;
    }
    getAllKeys(mode = StorageValueModes.Default) {
        if (!this.values) {
            throw Error('Attempting to get all keys before loading local storage.');
        }
        return Object.keys(this.values[this.domainKeyForMode(mode)]);
    }
    async removeValue(key, mode = StorageValueModes.Default) {
        if (!this.values) {
            throw Error(`Attempting to remove storage key ${key} before loading local storage.`);
        }
        const domain = this.values[this.domainKeyForMode(mode)];
        if (domain === null || domain === void 0 ? void 0 : domain[key]) {
            delete domain[key];
            return this.persistValuesToDisk();
        }
    }
    /**
     * Default persistence key. Platforms can override as needed.
     */
    getPersistenceKey() {
        return namespacedKey(this.identifier, RawStorageKey.StorageObject);
    }
    defaultValuesObject(wrapped, unwrapped, nonwrapped) {
        return DiskStorageService.DefaultValuesObject(wrapped, unwrapped, nonwrapped);
    }
    static DefaultValuesObject(wrapped = {}, unwrapped = {}, nonwrapped = {}) {
        return {
            [ValueModesKeys.Wrapped]: wrapped,
            [ValueModesKeys.Unwrapped]: unwrapped,
            [ValueModesKeys.Nonwrapped]: nonwrapped,
        };
    }
    domainKeyForMode(mode) {
        if (mode === StorageValueModes.Default) {
            return ValueModesKeys.Unwrapped;
        }
        else if (mode === StorageValueModes.Nonwrapped) {
            return ValueModesKeys.Nonwrapped;
        }
        else {
            throw Error('Invalid mode');
        }
    }
    /**
     * Clears simple values from storage only. Does not affect payloads.
     */
    async clearValues() {
        await this.setInitialValues();
        await this.immediatelyPersistValuesToDisk();
    }
    async getAllRawPayloads() {
        return this.device.getAllDatabaseEntries(this.identifier);
    }
    async getRawPayloads(uuids) {
        return this.device.getDatabaseEntries(this.identifier, uuids);
    }
    async savePayload(payload) {
        return this.savePayloads([payload]);
    }
    async savePayloads(payloads) {
        if (this.persistencePolicy === StoragePersistencePolicies.Ephemeral) {
            return;
        }
        const { encrypted, decrypted, deleted, discardable } = CreatePayloadSplitWithDiscardables(payloads);
        const rootKeyEncryptionAvailable = this.encryptionProvider.hasRootKeyEncryptionSource();
        const encryptable = [];
        const unencryptable = [];
        const { rootKeyEncryption, keySystemRootKeyEncryption, itemsKeyEncryption } = SplitPayloadsByEncryptionType(decrypted);
        if (itemsKeyEncryption) {
            extendArray(encryptable, itemsKeyEncryption);
        }
        if (keySystemRootKeyEncryption) {
            extendArray(encryptable, keySystemRootKeyEncryption);
        }
        if (rootKeyEncryption) {
            if (!rootKeyEncryptionAvailable) {
                extendArray(unencryptable, rootKeyEncryption);
            }
            else {
                extendArray(encryptable, rootKeyEncryption);
            }
        }
        if (discardable.length > 0) {
            await this.deletePayloads(discardable);
        }
        const encryptableSplit = SplitPayloadsByEncryptionType(encryptable);
        const keyLookupSplit = CreateEncryptionSplitWithKeyLookup(encryptableSplit);
        const encryptedResults = await this.encryptionProvider.encryptSplit(keyLookupSplit);
        const exportedEncrypted = [...encrypted, ...encryptedResults].map(CreateEncryptedLocalStorageContextPayload);
        const exportedDecrypted = unencryptable.map(CreateDecryptedLocalStorageContextPayload);
        const exportedDeleted = deleted.map(CreateDeletedLocalStorageContextPayload);
        return this.executeCriticalFunction(async () => {
            var _a;
            return (_a = this.device) === null || _a === void 0 ? void 0 : _a.saveDatabaseEntries([...exportedEncrypted, ...exportedDecrypted, ...exportedDeleted], this.identifier);
        });
    }
    async deletePayloads(payloads) {
        await this.deletePayloadsWithUuids(Uuids(payloads));
    }
    async deletePayloadsWithUuids(uuids) {
        await this.executeCriticalFunction(async () => {
            await Promise.all(uuids.map((uuid) => this.device.removeDatabaseEntry(uuid, this.identifier)));
        });
    }
    async deletePayloadWithUuid(uuid) {
        return this.executeCriticalFunction(async () => {
            await this.device.removeDatabaseEntry(uuid, this.identifier);
        });
    }
    async clearAllPayloads() {
        return this.executeCriticalFunction(async () => {
            return this.device.removeAllDatabaseEntries(this.identifier);
        });
    }
    clearAllData() {
        return this.executeCriticalFunction(async () => {
            await this.clearValues();
            await this.clearAllPayloads();
            await this.device.removeRawStorageValue(namespacedKey(this.identifier, RawStorageKey.SnjsVersion));
            await this.device.removeRawStorageValue(this.getPersistenceKey());
        });
    }
}
